import Icon from '@/components/snippets/icon/icon'
import { useSearchspringContext } from '../fetchers/provider'
import { useMemo, useState } from 'preact/hooks'
import { FilterItem, FilterSizeItem } from './FilterItem'
import { FilterSort } from './FilterSort'
import { useOption } from '../hooks/useOption'
import { FilterSelected } from './FilterSelected'
import { parseFiltersFromResults } from './FilterUtils'
import { useFilters } from '../hooks/useFilters'

export function FilterDrawer({ buttonClass = 'btn btn-text-black btn-md btn-inline underline' }) {
  const { results, refine, isLoading } = useSearchspringContext()
  const { handleToggleFilter, handleRemoveFilter, handleClearFilters, handleRemoveAllByField } = useFilters()

  const activeFilters = useOption('filters', {
    defaultValue: new Set(),
    syncWithURL: true
  })

  const { fetchedFilters, sizeFilters } = useMemo(() => parseFiltersFromResults(results), [results])

  // Handles persisting initial filter values and just disabling them
  const [initialFilters, setInitialFilters] = useState([])
  const [initialSizeFilters, setInitialSizeFilters] = useState([])

  // Handles active filter state and 'cancel' function
  const [restorableFilters, setRestorableFilters] = useState(new Set())

  const handleOpenFilters = () => {
    if (initialFilters.length === 0) setInitialFilters(fetchedFilters)
    if (initialSizeFilters.length === 0) setInitialSizeFilters(sizeFilters)
  }

  const handleCancelFilters = () => {
    refine('filters', restorableFilters)
    setRestorableFilters()
  }

  return (
    <>
      <x-modal className="group/filters" data-id="filters">
        <button
          className={buttonClass}
          data-modal-role="toggle"
          data-modal-parent="filters"
          onClick={handleOpenFilters}
        >
          <Icon id={'filter'} className="w-4 h-4" /> Filter {activeFilters?.size > 0 && <>({activeFilters.size})</>}
        </button>

        {/* Floating Mobile Filter bar */}
        <FixedFilterBarMobile activeFilters={activeFilters} handleOpenFilters={handleOpenFilters} />

        <button
          data-modal-role="close"
          data-modal-parent="filters"
          class="pointer-events-none fixed inset-0 z-max bg-black/60 opacity-0 group-data-visible/filters:opacity-100 md:z-20"
        ></button>
        <div className="fixed flex transition-opacity opacity-0 pointer-events-none inset-6 z-max group-data-visible/filters:pointer-events-auto group-data-visible/filters:opacity-100 md:z-50">
          <div className="relative flex flex-col w-full overflow-y-scroll transition-all -translate-x-4 rounded-lg bg-primary-white group-data-visible/filters:translate-x-0">
            <div className="grow flex flex-col divide-y divide-neutrals-medium-grey">
              <div className="flex justify-between px-4 py-2 pr-1 bg-primary-white md:px-6 md:py-4">
                <p className="my-auto h4">Filters</p>
                <button data-modal-role="close" data-modal-parent="filters" className="btn btn-md btn-icon btn-inline">
                  <Icon id="close" />
                </button>
              </div>
              <FilterSelected activeFilters={activeFilters} onRemove={handleRemoveFilter} onClear={handleClearFilters} />
              <div className="flex flex-col flex-grow overflow-y-scroll divide-y divide-neutrals-medium-grey">
                {initialFilters.map((filter) => (
                  <FilterItem
                    filter={filter}
                    onFilter={handleToggleFilter}
                    fetchedFilters={fetchedFilters}
                    activeFilters={activeFilters}
                    key={filter.field}
                    removeAll={handleRemoveAllByField}
                  />
                ))}
                {initialSizeFilters.length > 0 && (
                  <FilterSizeItem
                    initialFilters={initialSizeFilters}
                    onFilter={handleToggleFilter}
                    fetchedFilters={sizeFilters}
                    activeFilters={activeFilters}
                  />
                )}
              </div>
            </div>
            {typeof window !== 'undefined' && window.collection?.filters?.banner_image && (() => {
              const bannerUrl = window.collection.filters.banner_url
              const bannerImage = window.collection.filters.banner_image
              const bannerMobileImage = window.collection.filters.banner_mobile_image
              const className = `shrink-0 w-full h-fit my-6 md-max:px-4 overflow-hidden ${bannerMobileImage ? 'mob-alt_lg' : ''}`
              const imageContent = (
                <>
                  <img
                    src={bannerImage?.src || bannerImage}
                    alt=""
                    className="w-full h-fit"
                  />
                  {bannerMobileImage && (
                    <img
                      src={bannerMobileImage?.src || bannerMobileImage}
                      alt=""
                      className="w-full h-fit"
                    />
                  )}
                </>
              )

              return bannerUrl ? (
                <a href={bannerUrl} aria-label="Filters banner link" className={className}>
                  {imageContent}
                </a>
              ) : (
                <div className={className}>
                  {imageContent}
                </div>
              )
            })()}
            <div className="shrink-0 flex gap-4 p-4 bg-neutrals-base">
              <button
                data-modal-role="close"
                data-modal-parent="filters"
                className="btn btn-outline-black btn-md"
                onClick={handleCancelFilters}
              >
                Cancel
              </button>
              <button
                data-modal-role="close"
                data-modal-parent="filters"
                className="justify-center flex-grow btn btn-solid-black btn-md"
              >
                {activeFilters.size > 0 && results.pagination?.totalResults && !isLoading > 0
                  ? `View ${results.pagination?.totalResults} Results`
                  : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>
      </x-modal>
    </>
  )
}

const FixedFilterBarMobile = ({ activeFilters, handleOpenFilters }) => {
  return (
    <div class="md:hidden fixed flex py-3 bottom-4 right-4 z-30 bg-primary-blue rounded-lg border divide-x divide-primary-white border-neutrals-light-grey">
          <button
            className='h-auto px-4 py-1 underline btn btn-text-white btn-md btn-inline hover:text-primary-white'
            data-modal-role="toggle"
            data-modal-parent="filters"
            onClick={handleOpenFilters}
          >
            <Icon id={'filter'} className="w-4 h-4" />
            Filter {activeFilters?.size > 0 && <>({activeFilters.size})</>}
          </button>
          <FilterSort direction='up' buttonClass='underline btn btn-text-white btn-md h-auto btn-inline whitespace-nowrap hover:text-primary-white px-4 py-1' name={'fixed_mobile_sort'} />
        </div>
  )
}
